import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { invalidateMany } from './queryUtils';
import {
  getMatrix,
  getCellHistory,
  grantAccess,
  revokeAccess,
  retryAccess,
  bulkGrant,
  bulkRevoke,
  type AppAccessSystem,
  type AppAccessStatus,
  type AppAccessTier,
  type MatrixResponse,
  type HistoryRun,
  type BulkPair,
} from '../services/api/appAccess';

// ─────────────────────────────────────────────────────────────
// Matrix listing — paginated, URL-derived filters, keepPreviousData
// (Pattern from PendingTimesheetsPage.tsx:90-104)
// ─────────────────────────────────────────────────────────────

export interface AppAccessMatrixFilters {
  search: string;
  status: 'all' | AppAccessStatus;
  page: number;
  pageSize: number;
}

function hasPendingCell(data?: MatrixResponse): boolean {
  if (!data) return false;
  for (const row of data.data) {
    for (const cell of Object.values(row.cells)) {
      if (cell && cell.status === 'pending') return true;
    }
  }
  return false;
}

export function useAppAccessMatrix(filters: AppAccessMatrixFilters) {
  return useQuery<MatrixResponse>({
    queryKey: ['app-access-matrix', filters],
    queryFn: () => getMatrix(filters),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData, // T-09-04: prior data shown during pagination, no optimistic faking
    refetchInterval: (query) => (hasPendingCell(query.state.data) ? 5000 : false),
  });
}

// ─────────────────────────────────────────────────────────────
// Per-cell history — gated by enabled when ids are present
// (Pattern from useLeads.ts:30-36)
// ─────────────────────────────────────────────────────────────

export function useAppAccessHistory(
  userId: number | null,
  system: AppAccessSystem | null,
  pollWhilePending = false,
) {
  return useQuery<HistoryRun[]>({
    queryKey: ['app-access-history', userId, system],
    queryFn: () => getCellHistory(userId as number, system as AppAccessSystem),
    enabled: userId !== null && system !== null,
    staleTime: 10 * 1000,
    refetchInterval: pollWhilePending ? 5000 : false,
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations — server-confirm only (NO setQueryData / NO onMutate)
// Per D-13: invalidate matrix + per-cell history on success.
// Per Pitfall #4 / T-09-04: no optimistic update.
// ─────────────────────────────────────────────────────────────

// F8-004: every access-matrix mutation creates a new workflow_runs row via
// scheduleWorkflowRun on the backend, so the userWorkflowRuns query (read
// by the user-detail "Workflow Runs" panel) must be invalidated. Without
// it, the panel stays empty until manual refresh because
// useUserWorkflowRuns' polling is gated on a non-terminal run already
// loaded (F6-001 lesson). Mirrors F7-001 (delete/restore/create) and
// F7-006 (updateUser).
export function useGrantAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      system,
      tier,
    }: {
      userId: number;
      system: AppAccessSystem;
      tier?: AppAccessTier;
    }) => grantAccess(userId, system, tier),
    onSuccess: (_data, { userId, system }) => {
      invalidateMany(queryClient, [
        ['app-access-matrix'],
        ['app-access-history', userId, system],
        ['userWorkflowRuns', userId],
        ['userWorkflowRuns'],
      ]);
    },
  });
}

export function useRevokeAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      system,
      confirmation,
    }: {
      userId: number;
      system: AppAccessSystem;
      confirmation: string;
    }) => revokeAccess(userId, system, confirmation),
    onSuccess: (_data, { userId, system }) => {
      invalidateMany(queryClient, [
        ['app-access-matrix'],
        ['app-access-history', userId, system],
        ['userWorkflowRuns', userId],
        ['userWorkflowRuns'],
      ]);
    },
  });
}

export function useBulkGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pairs: BulkPair[]) => bulkGrant(pairs),
    onSuccess: () => {
      // Bulk has no single userId scope — global invalidation only.
      invalidateMany(queryClient, [
        ['app-access-matrix'],
        ['app-access-history'],
        ['userWorkflowRuns'],
      ]);
    },
  });
}

export function useBulkRevoke() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pairs: BulkPair[]) => bulkRevoke(pairs),
    onSuccess: () => {
      invalidateMany(queryClient, [
        ['app-access-matrix'],
        ['app-access-history'],
        ['userWorkflowRuns'],
      ]);
    },
  });
}

export function useRetryAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, system }: { userId: number; system: AppAccessSystem }) =>
      retryAccess(userId, system),
    onSuccess: (_data, { userId, system }) => {
      invalidateMany(queryClient, [
        ['app-access-matrix'],
        ['app-access-history', userId, system],
        ['userWorkflowRuns', userId],
        ['userWorkflowRuns'],
      ]);
    },
  });
}
